/**
 * headline.ts — the one-line value a COMPACT chip shows next to its domain
 * label, e.g. "4.6★ (27)", "12 upcoming", "$1,723". Pure + defensive: returns
 * '' when the payload has nothing sensible, and the chip renders just the label.
 */
import { CopilotWidget } from '../lib/copilotTypes'
import { data, items, money, num, oneDecimal, orDefault, statsData } from './format'

const firstNum = (blob: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const k of keys) {
    const v = num(blob[k])
    if (v !== undefined) return v
  }
  return undefined
}

export const buildHeadline = (w: CopilotWidget): string => {
  const d = data(w)
  const list = items(w)

  switch (w.type) {
    case 'review_stats': {
      const avg = oneDecimal(d.avg_rating)
      const count = num(d.count)
      if (avg && count !== undefined) return `${avg}★ (${count})`
      if (avg) return `${avg}★`
      return count !== undefined ? String(count) : ''
    }
    case 'review_list': {
      const count = orDefault(num(w.count), list.length)
      return count ? String(count) : ''
    }
    case 'booking_stats': {
      // real shape is `{ total, by_status }`; keep upcoming_count as a fallback.
      const total = firstNum(statsData(w), ['total', 'upcoming_count', 'upcoming'])
      return total !== undefined ? String(total) : ''
    }
    case 'booking_list': {
      const count = orDefault(num(w.count), list.length)
      return count ? String(count) : ''
    }
    case 'payment_stats': {
      // payments nest their numbers under `.data`; statsData merges them up.
      const total = firstNum(statsData(w), [
        'total',
        'total_amount',
        'gross_amount',
        'gross',
        'amount',
        'collected',
        'net'
      ])
      return total !== undefined ? money(total) : ''
    }
    case 'payment_list': {
      const sum = list.reduce((acc, it) => acc + (num(it.amount) || 0), 0)
      if (sum > 0) return money(sum)
      const count = orDefault(num(w.count), list.length)
      return count ? String(count) : ''
    }
    default:
      return ''
  }
}
